import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import {
  ArrowDownCircle,
  ArrowUpCircle,
  Wallet,
  Landmark,
  Banknote,
  TrendingUp,
  RefreshCw,
} from 'lucide-react';

interface Overview {
  periodo: { from: string; to: string };
  aReceber: { aluguel: number; avulsas: number; total: number };
  aPagar: { total: number };
  recebidoPeriodo: number;
  pagoPeriodo: number;
  saldoPeriodo: number;
  caixaHoje: { fisico: number; conta: number; total: number };
  banco?: { configurado: boolean; entradas: number; saidas: number; saldo: number; mensagem?: string | null };
}

const money = (v: number) =>
  'R$ ' + (v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 });

export default function PainelFinanceiro() {
  const [data, setData] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get('/finance/overview');
      setData(data);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
  }, []);

  const Card = ({
    title,
    value,
    sub,
    color,
    icon: Icon,
    to,
  }: {
    title: string;
    value: string;
    sub?: string;
    color: string;
    icon: any;
    to?: string;
  }) => {
    const inner = (
      <div className="bg-white dark:bg-dark-bg-secondary border border-gray-200 dark:border-dark-border rounded-lg p-5 h-full hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-500 dark:text-dark-text-secondary">{title}</span>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
        <div className={`text-2xl font-bold ${color}`}>{value}</div>
        {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
      </div>
    );
    return to ? <Link to={to}>{inner}</Link> : inner;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold dark:text-dark-text-primary flex items-center gap-2">
            <TrendingUp className="w-6 h-6" /> Painel Financeiro
          </h1>
          <p className="text-sm text-gray-500 dark:text-dark-text-secondary">
            Visão geral do mês {data ? `(${data.periodo.from} a ${data.periodo.to})` : ''}.
          </p>
        </div>
        <button onClick={load} className="p-2 text-gray-500 hover:text-gray-800" title="Atualizar">
          <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Card
          title="A Receber (total)"
          value={money(data?.aReceber.total || 0)}
          sub={`Aluguel ${money(data?.aReceber.aluguel || 0)} · Avulsas ${money(data?.aReceber.avulsas || 0)}`}
          color="text-emerald-600"
          icon={ArrowDownCircle}
          to="/contas"
        />
        <Card
          title="A Pagar (total)"
          value={money(data?.aPagar.total || 0)}
          color="text-red-600"
          icon={ArrowUpCircle}
          to="/contas"
        />
        <Card
          title="Saldo do mês (recebido − pago)"
          value={money(data?.saldoPeriodo || 0)}
          sub={`Recebido ${money(data?.recebidoPeriodo || 0)} · Pago ${money(data?.pagoPeriodo || 0)}`}
          color={(data?.saldoPeriodo || 0) >= 0 ? 'text-blue-600' : 'text-red-600'}
          icon={TrendingUp}
        />
      </div>

      <h2 className="text-lg font-semibold dark:text-dark-text-primary pt-2">Caixa de hoje</h2>
      <div className="grid md:grid-cols-3 gap-4">
        <Card title="Caixa físico (dinheiro/cheque)" value={money(data?.caixaHoje.fisico || 0)} color="text-emerald-600" icon={Banknote} to="/caixa" />
        <Card title="Conta (PIX/transf./cartão)" value={money(data?.caixaHoje.conta || 0)} color="text-blue-600" icon={Landmark} to="/caixa" />
        <Card title="Total recebido hoje" value={money(data?.caixaHoje.total || 0)} color="text-gray-800 dark:text-dark-text-primary" icon={Wallet} to="/caixa" />
      </div>

      <h2 className="text-lg font-semibold dark:text-dark-text-primary pt-2">Banco (conta real · Banking)</h2>
      {data?.banco?.configurado ? (
        <div className="grid md:grid-cols-3 gap-4">
          <Card title="Entradas no banco (mês)" value={money(data.banco.entradas)} color="text-emerald-600" icon={ArrowDownCircle} to="/bankconf" />
          <Card title="Saídas no banco (mês)" value={money(data.banco.saidas)} color="text-red-600" icon={ArrowUpCircle} to="/bankconf" />
          <Card title="Saldo do banco (mês)" value={money(data.banco.saldo)} color={data.banco.saldo >= 0 ? 'text-blue-600' : 'text-red-600'} icon={Landmark} to="/bankconf" />
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-lg px-4 py-3">
          O saldo bancário real ainda não está conectado. Configure a integração em{' '}
          <Link to="/bankconf" className="font-semibold underline">BankConf</Link> (C6: clientId/clientSecret + mTLS)
          para o painel exibir as entradas, saídas e saldo reais da conta.
        </div>
      )}

      <div className="flex flex-wrap gap-3 pt-2">
        <Link to="/charges" className="px-4 py-2 bg-primary-50 text-primary-700 rounded-lg text-sm font-medium">Cobranças (aluguel)</Link>
        <Link to="/contas" className="px-4 py-2 bg-emerald-50 text-emerald-700 rounded-lg text-sm font-medium">Contas a Pagar/Receber</Link>
        <Link to="/caixa" className="px-4 py-2 bg-amber-50 text-amber-700 rounded-lg text-sm font-medium">Caixa do dia</Link>
        <Link to="/finance" className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium">Financeiro (DRE/Fluxo)</Link>
      </div>
    </div>
  );
}
